import { CalendarDays } from "lucide-react";
import { useState, type ReactNode } from "react";

import { DayField } from "@/components/dates/DayField";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { parseDay, showDay, toDay } from "@/lib/calendar-dates";
import { cn } from "@/lib/utils";

/**
 * A start day and an end day, as one control (#555).
 *
 * Two clicks make a range: the first is one end, the second the other,
 * whichever order they come in. The caller hears nothing after the first —
 * half a range is not a range, and a report that redrew on it would be
 * showing dates nobody asked for. A third click starts again.
 *
 * `from` and `to` are `YYYY-MM-DD`, or `""` where there is none yet.
 */
export function DateRangePicker({
  id,
  from,
  to,
  onChange,
  min,
  max,
  placeholder = "Pick a date range",
  display,
  footer,
  keepOpen = false,
  className,
  ...aria
}: {
  id?: string;
  from: string;
  to: string;
  onChange: (range: { from: string; to: string }) => void;
  min?: string;
  max?: string;
  placeholder?: string;
  /** What the trigger says instead of the days alone — days with their times. */
  display?: string;
  /** More to choose under the calendar, such as a time. */
  footer?: ReactNode;
  /** Leave the popover open after a day is picked, when `footer` still has a choice in it. */
  keepOpen?: boolean;
  className?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  "aria-label"?: string;
}) {
  const [open, setOpen] = useState(false);
  // The first click of a new range, waiting for its second.
  const [anchor, setAnchor] = useState<string | null>(null);

  const earliest = parseDay(min);
  const latest = parseDay(max);
  const start = parseDay(anchor ?? from);
  const end = anchor === null ? parseDay(to) : undefined;

  function allowed(day: string): boolean {
    return (!min || day >= min) && (!max || day <= max);
  }

  function emit(a: string, b: string) {
    setAnchor(null);
    onChange(a <= b ? { from: a, to: b } : { from: b, to: a });
  }

  const label =
    from && to
      ? from === to
        ? showDay(from)
        : `${showDay(from)} – ${showDay(to)}`
      : from
        ? `From ${showDay(from)}`
        : to
          ? `Until ${showDay(to)}`
          : placeholder;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setAnchor(null);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          data-slot="date-range-picker"
          data-from={from}
          data-to={to}
          className={cn(
            "h-9 justify-start px-3 font-normal",
            !from && !to && "text-muted-foreground",
            className,
          )}
          {...aria}
        >
          <CalendarDays className="text-muted-foreground" />
          <span className="truncate">
            {(from || to) && display ? display : label}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto p-2"
        data-slot="date-range-popover"
      >
        <div className="flex items-center gap-2">
          <DayField
            label="From"
            value={anchor ?? from}
            accepts={allowed}
            onCommit={(day) => {
              // With no end yet it waits for one, like a first click. With
              // one, a start typed past it takes the end along, rather than
              // being refused for a date that is about to be changed.
              if (to === "") setAnchor(day);
              else emit(day, to < day ? day : to);
            }}
          />
          <span className="text-muted-foreground text-xs">to</span>
          <DayField
            label="To"
            value={to}
            accepts={allowed}
            onCommit={(day) => {
              // Either order: an end typed before the start swaps with it.
              const other = anchor ?? from;
              emit(other === "" ? day : other, day);
            }}
          />
        </div>
        <Calendar
          mode="range"
          numberOfMonths={2}
          // With two months side by side, a neighbouring month's days would
          // show the same range twice.
          showOutsideDays={false}
          selected={start ? { from: start, to: end ?? start } : undefined}
          defaultMonth={parseDay(from) ?? latest}
          startMonth={earliest}
          endMonth={latest}
          disabled={[
            ...(earliest ? [{ before: earliest }] : []),
            ...(latest ? [{ after: latest }] : []),
          ]}
          onSelect={(_, clicked) => {
            const day = toDay(clicked);
            if (anchor === null) {
              setAnchor(day);
              return;
            }
            emit(anchor, day);
            if (!keepOpen) setOpen(false);
          }}
        />
        <p className="text-muted-foreground px-1 pt-1 text-xs">
          {anchor === null
            ? "Pick the first day of the range."
            : "Now pick the last day."}
        </p>
        {footer}
      </PopoverContent>
    </Popover>
  );
}
